'use client'

import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'

/**
 * Skeleton placeholder for the right-column "Your Collateral" breakdown while the
 * card's sub-account data (account liquidity + enabled-collateral set) is still
 * loading. Prevents flashing the "Collateral will be available..." or
 * "Savings in this Position" sections with stale / partial data when the user
 * switches between positions.
 */
export function EulerCollateralBreakdownSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="flex animate-pulse flex-col gap-5">
      <Section label="Enabled Collateral" sublabel="Counts toward spending power" rows={rows} />
      <Section label="Savings in this Position" sublabel="Deposited but not used as collateral" rows={Math.max(rows - 1, 1)} withAction />
    </div>
  )
}

function Section({
  label,
  sublabel,
  rows,
  withAction,
}: {
  label: string
  sublabel: string
  rows: number
  withAction?: boolean
}) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <Typography variant="p" className="font-semibold text-sm">
          {label}
        </Typography>
        <Typography variant="p" className="text-muted-foreground text-xs">
          {sublabel}
        </Typography>
      </div>
      <div className="flex flex-col">
        <div
          className={cn(
            'grid gap-3 border-border/50 border-b px-2 py-2',
            withAction ? '[grid-template-columns:2fr_1fr_1fr_auto]' : 'grid-cols-3',
          )}
        >
          <div className="h-3 w-16 rounded bg-muted/60" />
          <div className="h-3 w-14 rounded bg-muted/60" />
          <div className="h-3 w-20 justify-self-end rounded bg-muted/60" />
          {withAction && <div className="h-3 w-12 justify-self-end rounded bg-muted/60" />}
        </div>
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'grid items-center gap-3 border-border/30 border-b px-2 py-3 last:border-b-0',
              withAction ? '[grid-template-columns:2fr_1fr_1fr_auto]' : 'grid-cols-3',
            )}
          >
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 shrink-0 rounded-full bg-muted" />
              <div className="flex flex-col gap-1">
                <div className="h-3 w-20 rounded bg-muted/70" />
                <div className="h-2 w-24 rounded bg-muted/50" />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="h-3 w-16 rounded bg-muted/70" />
              <div className="h-2 w-12 rounded bg-muted/50" />
            </div>
            <div className="h-3 w-14 justify-self-end rounded bg-muted/70" />
            {withAction && <div className="h-6 w-20 justify-self-end rounded-md bg-muted/70" />}
          </div>
        ))}
      </div>
    </div>
  )
}
